import { useState } from "react"
import { useMutation } from "urql"

import { BatchDeleteProductsFromEquivalencyGroupDocument } from "../graphql/removeProductsFromEquivalencyGroup.mutation"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { type AssociatedProduct, isAkrochemProduct } from "@/utils/equivalentProducts"

interface UseRemoveProductFromEquivalencyGroupParams {
  equivalencyGroupId: string
  onSuccess?: () => void
}

interface UseRemoveProductFromEquivalencyGroupReturn {
  removeProduct: (product: AssociatedProduct) => Promise<void>
  isRemoving: boolean
}

export const useRemoveProductFromEquivalencyGroup = ({
  equivalencyGroupId,
  onSuccess,
}: UseRemoveProductFromEquivalencyGroupParams): UseRemoveProductFromEquivalencyGroupReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isRemoving, setIsRemoving] = useState(false)
  const [, executeMutation] = useMutation(BatchDeleteProductsFromEquivalencyGroupDocument)

  const removeProduct = async (product: AssociatedProduct) => {
    setIsRemoving(true)

    try {
      const isAkrochem = isAkrochemProduct(product)
      const isCompetitor = !isAkrochem
      const input = {
        equivalencyGroupId,
        productIds: isAkrochem ? [product.productId] : [],
        competitorProductIds: isCompetitor ? [product.competitorProductId] : [],
      }

      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Failed to remove product. Please try again.",
        })
        return
      }

      const data = result.data?.equivalencyGroup?.batchDeleteProductsFromEquivalencyGroup

      if (data?.__typename === "EquivalencyGroupFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: data.error.message,
        })
        return
      }

      if (data?.__typename === "BatchDeleteProductsFromEquivalencyGroupSuccess") {
        notify({
          type: NotificationType.Success,
          silent: false,
          message: "Product removed successfully.",
        })
        onSuccess?.()
      }
    } finally {
      setIsRemoving(false)
    }
  }

  return {
    removeProduct,
    isRemoving,
  }
}
